import classnames from 'classnames';
import { all, equals, forEach, innerJoin, isEmpty, length, map, not, path, propOr } from 'ramda';
import React, { CSSProperties, FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Divider, EmptyState as WSREmptyState, FloatingNotification } from '@wix/design-system';
import { Tooltip, WixIcons } from '../../../../components/common';
import { Breadcrumbs } from '../../../../components/common/Breadcrumbs';
import { FullHeightLoader } from '../../../../components/common/FullHeightLoader';
import { Lasso } from '../../../../components/common/Lasso';
import lassoSelectedAreaStyles from '../../../../components/common/Lasso/withLassoSelectedAreaStyle/LassoSelectedArea.scss';
import { Loader } from '../../../../components/common/Loader';
import { RtlSearch } from '../../../../components/common/Search';
import { BiAddMediaActionTarget } from '../../../../constants/Bi';
import { ItemType } from '../../../../constants/ItemType';
import { LassoItemMargin } from '../../../../constants/Lasso';
import { Layout, SettingsLayout } from '../../../../constants/Layout';
import { ExternalSource } from '../../../../constants/Source';
import { SystemFolderId } from '../../../../constants/SystemFolderId';
import { generateRandomGuid4 } from '../../../../monitoring/utils';
import { createFilesBiOptionsMap, getItemFormat } from '../../../../utils/bi';
import { useMMGRSettings } from '../../../../utils/hoc/withMMGRSettings';
import { useRootFolder } from '../../../../utils/hoc/withRootFolder';
import { useTrackAddMediaImportClickAnalytics } from '../../../../utils/hooks/bi/useTrackAddMediaImportClickAnalytics';
import { useEffectButSkipMount } from '../../../../utils/hooks/useEffectButSkipMount';
import { MediaManagerSDKFile, MediaManagerSDKFolder } from '../../../../utils/media-manager-sdk';
import { WixSite } from '@wix/media-manager-sdk';
import {
  canItemsBeShared,
  decodeWixSiteFolderId,
  isAllWixMediaRootFolder,
  isFile,
  isItemImportable,
  isPurchasedFromShutterstock,
  isSiteFilesRootFolderId,
} from '../../../../utils/media-manager-sdk/items';
import { MediaTypeSelect } from '../../../FilesManager/MediaTypeSelect';
import { ExternalSourcesConfigs, FilesViewerView } from '../../constants/Sources';
import { EmptyState } from '../FilesViewer/EmptyState';
import styles from '../FilesViewer/FilesViewer.scss';
import { FilesViewerFooter } from '../FilesViewer/FilesViewerFooter';
import { FilesGallery } from '../FilesViewer/layouts/FilesGalleryLayout';
import { GalleryLayout } from '../FilesViewer/layouts/GalleryLayout';
import { extractSiteFolder, generateFooterMessage, isInsideWixVideoLibrary } from '../FilesViewer/utils';
import { IInner, IOuter } from './Connected.types';
import { SortSwitcher } from '../../../FilesManager/SortSwitcher';
import { LayoutSwitcher } from '../../../FilesManager/LayoutSwitcher';
import { SmallListFolder } from '../../../../components/layouts/List/SmallListFolder';
import { Tabnames } from '../../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { NetworkUnstable } from '../../../../components/common/EmptyStateImages';
import { useTrackErrorStateAnalytics } from '../../../../utils/hooks/bi/useTrackErrorStateAnalytics';
import { getErrorMessage } from '../../../../utils/hooks/useLoadingErrorCatcher/useLoadingErrorCatcher.utils';
import { ItemSelectOrigin } from '../../../../utils/hooks/bi/useTrackItemSelectAnalytics/types';
import { useTrackItemSelectAnalytics } from '../../../../utils/hooks/bi/useTrackItemSelectAnalytics';

const scrollStyle: CSSProperties = { position: 'relative', overflowX: 'hidden', flexDirection: 'column' };

export const ConnectedComponent: FC<IOuter & IInner> = ({
  source,
  selectedItems,
  refreshFolder,
  openFolder: openFolderFromProps,
  onSelect,
  openedFrom,
  className,
  folderPath,
  currentFolder,
  items,
  isLoading,
  isWixSitesSource,
  isSearch,
  toggleItemSelection,
  selectItemsUntil,
  selectItem,
  selectItems,
  setSiteFolder,
  mediaTypes,
  setMediaType,
  areNotAllMediaTypesSelected,
  selectAllMediaTypes,
  areNextFilesLoading,
  hasMoreItems,
  loadMoreItems,
  selectedItemsCount,
  isItemSelected,
  clearItemsSelection,
  isNotAuthorized,
  onSourceSessionExpired,
  searchQuery,
  setSearchQuery,
  clearSearch,
  siteSorting,
  setSiteSorting,
  siteLayout,
  setSiteLayout,
  loadingError,
}) => {
  useEffectButSkipMount(refreshFolder, [mediaTypes]);

  const { formatMessage } = useIntl();
  const { trackAddMediaImportClick } = useTrackAddMediaImportClickAnalytics();
  const { trackErrorScreenRendered } = useTrackErrorStateAnalytics();
  const { trackItemsSelected } = useTrackItemSelectAnalytics();

  useEffect(() => {
    if (loadingError) {
      trackErrorScreenRendered(
        source,
        siteLayout === Layout.Gallery ? SettingsLayout.GalleryWithPanel : SettingsLayout.ListWithPanel,
        getErrorMessage(loadingError),
        'connected-empty-state-server-error',
      );
    }
  }, [loadingError]);

  const rootFolder = useRootFolder(source);

  const [showSearchTooltip, setTooltipVisibility] = useState(true);
  const tooltipZindex = showSearchTooltip ? 6000 : 0;

  const {
    mmgrSettings: { team },
  } = useMMGRSettings();

  const shouldApplyNewLayout = isWixSitesSource;
  const isAgency = Boolean(team);

  const openFolder = useCallback(
    (folder) => {
      clearSearch();
      openFolderFromProps(folder);
    },
    [openFolderFromProps],
  );

  const setSiteFolderOnWixSiteSource = useCallback(() => {
    const possibleSiteFolder = extractSiteFolder(folderPath);

    if (possibleSiteFolder) {
      const decodedId = decodeWixSiteFolderId(possibleSiteFolder.id);

      if (decodedId.siteMediaToken && isSiteFilesRootFolderId(decodedId.id)) {
        setSiteFolder(possibleSiteFolder);
      }

      return;
    }

    setSiteFolder(null);
  }, [folderPath, setSiteFolder]);

  const isSingleLevelNesting = equals(length(folderPath), 1);
  const currentFolderId = propOr('', 'id', currentFolder);

  const isRoot = isSingleLevelNesting || (!isSingleLevelNesting && isAllWixMediaRootFolder(currentFolderId));
  const isWixSitesRoot = isWixSitesSource && equals(currentFolderId, SystemFolderId.WixSites);
  const isPurchasedFolder =
    isWixSitesSource && equals(propOr('', 'id', decodeWixSiteFolderId(currentFolderId)), SystemFolderId.Purchased);

  const insideWixVideoLibrary = isInsideWixVideoLibrary(folderPath);

  const rootLayout =
    (isWixSitesRoot && (siteLayout as unknown as FilesViewerView)) ||
    path([source, 'view', 'root'], ExternalSourcesConfigs);
  const nestedLayout = path([source, 'view', 'nested'], ExternalSourcesConfigs);

  const hasNoItems = !isLoading && !items.length;

  const isSearchVisible = !isWixSitesRoot;
  const isGalleryLayout = isRoot ? rootLayout === FilesViewerView.Gallery : nestedLayout === FilesViewerView.Gallery;
  const isListLayout = isWixSitesRoot && !isGalleryLayout;
  const isBreadcrumbsVisible = isWixSitesSource || !(hasNoItems && !isSearch && isRoot);

  const canAllItemsBeImported = useMemo(() => {
    if (isAgency && !canItemsBeShared(selectedItems, team)) {
      return false;
    }
    return all(isItemImportable, selectedItems);
  }, [selectedItems, isAgency]);

  const canImportSelectedItems = canAllItemsBeImported && not(isEmpty(selectedItems));
  const footerMessage = canAllItemsBeImported
    ? null
    : generateFooterMessage(selectedItems, { rootFolderName: rootFolder.name });

  const chooseFiles = useCallback(
    (items) => {
      const batchId = generateRandomGuid4();

      const itemsWithCorrelationIds = map<MediaManagerSDKFile, MediaManagerSDKFile>((item) => {
        const correlationId = generateRandomGuid4();
        const fileFormat = getItemFormat(item);
        const mediaType = isFile(item) ? item.mediaType : ItemType.Folder;

        return {
          ...item,
          name: item.name || formatMessage({ id: 'files-importer.untitled-file-name' }, { source }),
          bi: {
            ...item.bi,
            // add `correlationId` and `batchId` to file's model.
            // It will be used later in add started/finished (and all intermediate) events
            correlationId,
            batchId,
            fileFormat,
            mediaType,
          },
        };
      }, items);

      forEach<MediaManagerSDKFile, MediaManagerSDKFile[]>((item) => {
        const { correlationId, batchId, fileFormat, mediaType } = item.bi;
        const batchSize = length(items);

        trackAddMediaImportClick({
          correlationId,
          batchId,
          batch: batchSize > 1,
          batchSize,
          fileFormat,
          mediaType,
          target: item.source === ExternalSource.WixVideo ? BiAddMediaActionTarget.WixVideoLibrary : source,
        });
      }, itemsWithCorrelationIds);

      onSelect(itemsWithCorrelationIds, {
        bi: {
          files: createFilesBiOptionsMap(itemsWithCorrelationIds, openedFrom, source),
        },
      });
    },
    [source, onSelect, trackAddMediaImportClick, openedFrom],
  );

  const submitSelectedItems = useCallback(() => chooseFiles(selectedItems), [chooseFiles, selectedItems]);
  const submitItemOnDoubleClick = useCallback((item) => chooseFiles([item]), [chooseFiles]);
  const allSitesFolder: MediaManagerSDKFolder & { hideFromBreadcrumbs: boolean } = {
    kind: ItemType.Folder,
    id: SystemFolderId.WixSites,
    source: ExternalSource.WixSite,
    parentId: WixSite.oldMediaManagerFolder.id,
    name: WixSite.oldMediaManagerFolder.name,
    hideFromBreadcrumbs: true,
    siteId: null,
  };

  const handleBreadcrumbsClick = useCallback(
    (folder) => {
      clearSearch();
      openFolder(isAllWixMediaRootFolder(folder.id) ? allSitesFolder : folder);
    },
    [openFolder],
  );

  const biParams = useMemo(() => ({ tabName: `upload_widget_${source}` }), [source]);

  const handleGalleryLayoutItemClick = useCallback(
    (item) => toggleItemSelection(item.id, biParams),
    [toggleItemSelection, biParams],
  );

  const handleFilesGalleryItemClick = useCallback(
    (item, event: MouseEvent<HTMLElement>) => {
      const { id } = item;
      const { ctrlKey, metaKey, shiftKey } = event;

      if (ctrlKey || metaKey) {
        toggleItemSelection(id, biParams);
      } else if (shiftKey) {
        selectItemsUntil(id);
      } else {
        selectItem(id, biParams);
      }
    },
    [toggleItemSelection, selectItemsUntil, selectItem, biParams],
  );

  const handleLassoSelectFinish = useCallback(
    (itemIds: string[]) => {
      const itemsToTrack = innerJoin((item, id) => item.id === id, items, itemIds);
      trackItemsSelected(itemsToTrack, { clickOrigin: ItemSelectOrigin.Lasso });
    },
    [items, trackItemsSelected],
  );

  const handleSearchStart = useCallback(
    (searchQuery) => {
      const siteFolder = extractSiteFolder(folderPath);

      if (!isInsideWixVideoLibrary(folderPath) && siteFolder) {
        openFolder(siteFolder);
      }

      setSearchQuery(searchQuery);
    },
    [openFolder, folderPath],
  );

  useEffect(() => {
    if (isWixSitesSource) {
      setSiteFolderOnWixSiteSource();
    }
  }, [isWixSitesSource, setSiteFolderOnWixSiteSource]);

  useEffect(() => {
    if (isNotAuthorized) {
      onSourceSessionExpired(source);
    }
  }, [isNotAuthorized, onSourceSessionExpired, source]);

  const isItemDisabled = (item) => {
    return isAgency && (isPurchasedFolder || isPurchasedFromShutterstock(item));
  };

  // handles FilesImporter's complex JSON IDs
  const customIsItemSelected = useCallback(
    (itemId: string) => {
      if (isWixSitesRoot) {
        // On WixSites root, items are sites with simple IDs, not JSON
        return selectedItems.some((selectedItem) => selectedItem.id === itemId);
      }

      let normalizedId = itemId;
      try {
        const parsed = JSON.parse(itemId);
        normalizedId = parsed.id ?? itemId;
      } catch {
        // If parsing fails, use the original ID
        normalizedId = itemId;
      }

      // Check if the normalized ID exists in selectedItems
      return selectedItems.some((selectedItem) => {
        try {
          const parsedSelected = JSON.parse(selectedItem.id);
          return parsedSelected.id === normalizedId;
        } catch {
          return selectedItem.id === normalizedId;
        }
      });
    },
    [selectedItems, isWixSitesRoot],
  );

  const searchBIParams = { tabName: Tabnames.ImportFromWix };

  return (
    <div className={classnames(styles.wrapper, className)}>
      {isBreadcrumbsVisible && (
        <div className={styles.topRow}>
          <Breadcrumbs
            className={styles.breadcrumbs}
            folderPath={folderPath}
            source={source}
            onClick={handleBreadcrumbsClick}
            dropDisabled
          />
          <div className={styles.topRowActions}>
            {isWixSitesRoot && (
              <>
                <Box align="right">
                  <Tooltip
                    content={<FormattedMessage id="search-switcher.hint" />}
                    inline
                    size="small"
                    zIndex={tooltipZindex}
                    onHide={() => setTooltipVisibility(true)}
                  >
                    <RtlSearch
                      isExpandable
                      value={searchQuery}
                      onEnterPressed={handleSearchStart}
                      onClearButtonClick={clearSearch}
                      placeholder={formatMessage({ id: 'search.input-placeholder' })}
                      biParams={searchBIParams}
                      className={styles.searchSitesInput}
                      onInputClicked={() => setTooltipVisibility(false)}
                    />
                  </Tooltip>
                </Box>
                <Box height="16px" paddingLeft="6px" paddingRight="6px">
                  <Divider direction="vertical" />
                </Box>
                <Box paddingRight="6px">
                  <SortSwitcher sort={siteSorting} onSortChange={setSiteSorting} tabName={Tabnames.ImportFromWix} />
                </Box>
                <LayoutSwitcher layout={siteLayout} onLayoutChange={setSiteLayout} tabName={Tabnames.ImportFromWix} />
              </>
            )}

            {isWixSitesSource && isSearchVisible && (
              <>
                <div
                  className={classnames(styles.searchWrapper, {
                    [styles.searchWrapperInNewLayout]: shouldApplyNewLayout,
                  })}
                >
                  <RtlSearch
                    value={searchQuery}
                    onEnterPressed={handleSearchStart}
                    onClearButtonClick={clearSearch}
                    isAlwaysActive={!shouldApplyNewLayout}
                    key={searchQuery}
                    className={styles.searchSitesInput}
                    placeholder={formatMessage({ id: 'search.input-placeholder' })}
                    isExpandable
                  />
                </div>
                {shouldApplyNewLayout && <div className={styles.actionsDivider} />}
                <MediaTypeSelect
                  mediaTypes={mediaTypes}
                  onMediaTypeSelect={setMediaType}
                  forceWithIcon
                  isDisabled={(hasNoItems && !areNotAllMediaTypesSelected) || insideWixVideoLibrary}
                />
              </>
            )}
          </div>
        </div>
      )}

      {isAgency && isPurchasedFolder && (
        <FloatingNotification
          type="warning"
          prefixIcon={<WixIcons.StatusAlert />}
          width="100%"
          showCloseButton={false}
          text={<FormattedMessage id="files-importer.purchased-file.prohibit-share.toast" />}
        />
      )}

      {loadingError ? (
        <Box verticalAlign="middle" align="center" height="100%">
          <WSREmptyState
            skin="page-no-border"
            title={<FormattedMessage id="empty-state.server-error-title" />}
            subtitle={<FormattedMessage id="empty-state.server-error-subtitle" />}
            image={NetworkUnstable}
            align="center"
            dataHook="connected-empty-state-server-error"
          />
        </Box>
      ) : hasNoItems ? (
        <EmptyState
          source={source}
          isSearch={isSearch}
          searchQuery={searchQuery}
          isRoot={isRoot}
          isWixSitesRoot={isWixSitesRoot}
          areNotAllMediaTypesSelected={areNotAllMediaTypesSelected}
          clearAllExtendedFilters={selectAllMediaTypes}
        />
      ) : (
        <div className={styles.content}>
          <div className={classnames(styles.items, isLoading && styles.isLoadingItems)}>
            {isLoading ? (
              <div className={styles.loader}>
                <FullHeightLoader />
              </div>
            ) : (
              <Lasso
                itemMarginRight={LassoItemMargin[Layout.Gallery]}
                selectedItems={selectedItems}
                onSelectItems={selectItems}
                onSelectFinish={handleLassoSelectFinish}
              >
                {({ saveScrollingComponentWrapperRef, selectedAreaStyle }) => (
                  <div className={styles.scrollArea}>
                    <InfiniteScroll
                      ref={saveScrollingComponentWrapperRef}
                      scrollThreshold="400px"
                      height="100%"
                      dataLength={items.length}
                      hasMore={hasMoreItems}
                      next={loadMoreItems}
                      style={scrollStyle}
                      loader={null}
                    >
                      {isListLayout ? (
                        map(
                          (item) => (
                            <SmallListFolder
                              key={item.id}
                              item={item as MediaManagerSDKFolder}
                              selected={customIsItemSelected(item.id)}
                              onClick={openFolder}
                              showCheckbox={false}
                              withoutChevron
                            />
                          ),
                          items,
                        )
                      ) : isGalleryLayout && !isWixSitesRoot ? (
                        <GalleryLayout
                          items={items as MediaManagerSDKFile[]}
                          isItemSelected={customIsItemSelected}
                          isItemDisabled={isItemDisabled}
                          onItemClick={handleGalleryLayoutItemClick}
                          onItemDoubleClick={submitItemOnDoubleClick}
                        />
                      ) : (
                        <FilesGallery
                          items={items}
                          isItemSelected={customIsItemSelected}
                          isItemDisabled={isItemDisabled}
                          onFolderDoubleClick={openFolder}
                          onFileDoubleClick={submitItemOnDoubleClick}
                          clearItemsSelection={clearItemsSelection}
                          onItemClick={handleFilesGalleryItemClick}
                          isFilesImporterNewLayout={shouldApplyNewLayout}
                        />
                      )}

                      {areNextFilesLoading && (
                        <div className={styles.loaderMore}>
                          <Loader size="small" />
                        </div>
                      )}

                      {selectedAreaStyle && (
                        <div
                          className={lassoSelectedAreaStyles.selectedArea}
                          style={selectedAreaStyle}
                          data-element-type="lasso"
                        />
                      )}
                    </InfiniteScroll>
                  </div>
                )}
              </Lasso>
            )}
          </div>

          <FilesViewerFooter
            selectedItemsCount={selectedItemsCount}
            onImportButtonClick={submitSelectedItems}
            isButtonDisabled={!canImportSelectedItems || (isAgency && isPurchasedFolder)}
            message={footerMessage}
          />
        </div>
      )}
    </div>
  );
};
